import { DemonstrativoReceitaEDespesaAnualItem, FiltroRelatorioReceitaDespesaAnual } from "core/features/relatorios/typings";
import {
  RelatorioDemonstrativoReceitaDespesaTotais,
  DemonstrativoReceitaEDespesaTransferencia,
} from "core/features/relatorios/typings";
import xlsx, { ISettings, IJsonSheet, IContent } from "json-as-xlsx";
import moment from "moment";
import {defaultContentMapperXlsx} from 'infra/packages/json-as-xlsx/index';
import { formatMoney } from 'infra/helpers/number';


export interface TabelaCategoriasRelatorioDemonstrativoReceitaDespesaAnualRow extends Record<string, string | number | boolean> {
  id: string;
  breadcrumb: string;
  nome: string;
  porcentagem: string | number;
  valor: string | number;
  media: string | number;
  mediaTotal: string | number;
  nivel: number;
  totalRow: boolean;
  hasChildren: boolean;
}

export function flattenRelatorioItems(
  items: DemonstrativoReceitaEDespesaAnualItem[],
  nivel = 0,
  includeTotalRow = true,
) {
  let _items: (DemonstrativoReceitaEDespesaAnualItem & { nivel: number; totalRow?: boolean })[] = [];

  items.forEach((item) => {
    _items.push({ ...item, nivel });

    const children = flattenRelatorioItems(item.items, nivel + 1);

    _items.push(...children);

    if (item.items?.length && includeTotalRow) {
      _items.push({
        ...item,
        nivel,
        breadcrumb: "",
        nome: `Total de ${item.nome}`,
        items: [],
        valor: item.valor + item.valorItems,
        totalRow: true,
      })
    }
  });

  return _items;
}

export const formatterDemonstrativoReceitaDespesaAnual = (item: DemonstrativoReceitaEDespesaAnualItem & {nivel?: number; totalRow?: boolean}) => {
  const obj: TabelaCategoriasRelatorioDemonstrativoReceitaDespesaAnualRow = {
    id: `${item.id}-${item.nome}`,
    breadcrumb: item.breadcrumb,
    nome: item.nome,
    porcentagem: item.valor ? item.porcentagem : '',
    valor: item.valor ? item.valor : '',
    media: item.valor ? item.media : '',
    mediaTotal: item.mediaTotal,
    nivel: item.nivel || 0,
    totalRow: !!item.totalRow,
    hasChildren: !!item.items?.length,
  };
  
  if(!item.items?.length || item.valor) {
    item.meses?.forEach((mes, index) => obj[`mes-${index}`] = item.totalRow ? mes.total : mes.valor);
  }

  return obj;
};

export function exportExcelRelatorioDemonstrativoReceitaDespesa({
  receitas,
  despesas,
  filtro,
  totais,
  transferencias,
}: {
  receitas: DemonstrativoReceitaEDespesaAnualItem[];
  despesas: DemonstrativoReceitaEDespesaAnualItem[];
  filtro: FiltroRelatorioReceitaDespesaAnual;
  totais: RelatorioDemonstrativoReceitaDespesaTotais;
  transferencias?: DemonstrativoReceitaEDespesaTransferencia[];
}) {
  const ano = filtro.ano;
  const dataInicio = `${filtro.ano}-01-01`;

  const settings: ISettings = {
    fileName: `Relatório Demonstrativo de Receitas e Despesas Anual - ${filtro.ano}`,
    extraLength: 3,
    writeOptions: {},
  };

  const columnsReceitasDespesas = getColumnsReceitasDespesas(ano);

  const columnsTransferencias = getColumnsTransferencias();

  const sheetsData: IJsonSheet[] = [
    {
      sheet: "Geral",
      columns: columnsReceitasDespesas,
      content: [{} as any, ...receitas, {} as any, ...despesas].map(
        formatterDemonstrativoReceitaDespesaAnual
      ),
    },
    {
      sheet: "Receitas",
      columns: columnsReceitasDespesas,
      content: receitas.map(formatterDemonstrativoReceitaDespesaAnual),
    },
    {
      sheet: "Despesas",
      columns: columnsReceitasDespesas,
      content: despesas.map(formatterDemonstrativoReceitaDespesaAnual),
    },
  ];

  if (transferencias?.length) {
    sheetsData.push({
      sheet: "Transferências",
      columns: columnsTransferencias,
      content: transferencias.map(defaultContentMapperXlsx),
    });
  }

  sheetsData.push({
    sheet: "Outros dados",
    content: [{}],
    columns: [
      {
        label: `Saldo em ${moment(dataInicio).add(-1, 'days').format("DD/MM/YYYY")}${
          moment(dataInicio).isAfter(moment().endOf("day"))
            ? " (Previsto)"
            : ""
        }`,
        value: () => `${formatMoney(totais.saldoInicial || 0)}`,
      },
      {
        label: "Resultado",
        value: () => `${formatMoney(totais.resultado || 0)}`,
      },
    ],
  });

  xlsx(sheetsData, settings);
}

function getColumnsTransferencias() {
  return [
    {
      label: "Data",
      value: (row: IContent) =>
        `${row.data ? moment(row.data as string).format("DD/MM/YYYY") : ""}`,
    },
    {
      label: "Valor",
      format: "#,###0.00",
      value: (row: IContent) => (!row.valor ? "" : +row.valor),
    },
    {
      label: "Conta de Origem",
      value: (row: IContent) => (!row.contaOrigem ? "" : row.contaOrigem),
    },
    {
      label: "Conta de Destino",
      value: (row: IContent) => (!row.contaDestino ? "" : row.contaDestino),
    },
  ];
}


function getColumnsReceitasDespesas(ano: string) {
  return [
    {
      label: "Conta Categoria",
      value: (row: IContent) =>
        `${row.breadcrumb ? row.breadcrumb + "." : ""}${row.nome ?? ""}`,
    },
    {
      label: `Jan/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-0'] === undefined ? "" : +row['mes-0']),
    },
    {
      label: `Fev/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-1'] === undefined ? "" : +row['mes-1']),
    },
    {
      label: `Mar/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-2'] === undefined ? "" : +row['mes-2']),
    },
    {
      label: `Abr/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-3'] === undefined ? "" : +row['mes-3']),
    },
    {
      label: `Mai/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-4'] === undefined ? "" : +row['mes-4']),
    },
    {
      label: `Jun/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-5'] === undefined ? "" : +row['mes-5']),
    },
    {
      label: `Jul/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-6'] === undefined ? "" : +row['mes-6']),
    },
    {
      label: `Ago/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-7'] === undefined ? "": +row['mes-7']),
    },
    {
      label: `Set/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-8'] === undefined ? "" : +row['mes-8']),
    },
    {
      label: `Out/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-9'] === undefined ? "" : +row['mes-9']),
    },
    {
      label: `Nov/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-10'] === undefined ? "" : +row['mes-10']),
    },
    {
      label: `Dez/${ano}`,
      format: "#,###0.00",
      value: (row: IContent) => (row['mes-11'] === undefined ? "" : +row['mes-11']),
    },
    {
      label: "Total",
      format: "#,###0.00",
      value: (row: IContent) => (row.valor ?? ""),
    },
    {
      label: "Média",
      format: "#,###0.00",
      value: (row: IContent) => (row.media ?? ""),
    },
  ];
}
